import { useState, useEffect } from "react";
import { Search, X, RefreshCw, ClipboardList } from "lucide-react";

import { assessments as assessmentsApi } from "../lib/api.js";
import { useApi, useDebounce } from "../hooks/index.js";
import { Spinner, Empty, CloseX, Pagination } from "../components/ui.jsx";
import { useLang } from "../lib/i18n.jsx";

function fmtTime(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function domainAvg(domainScores) {
  const values = Object.values(domainScores ?? {}).filter(v => typeof v === "number");
  if (values.length === 0) return 0;
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

function scoreBadgeClass(pct) {
  if (pct >= 70) return "badge bg-green";
  if (pct >= 45) return "badge bg-orange";
  return "badge bg-red";
}

// ─── DETAIL MODAL ─────────────────────────────────────────────────────────────

function DetailModal({ assessment, onClose }) {
  const { t } = useLang();
  const a = assessment;
  const pct = domainAvg(a.domainScores);

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-ttl">
          {a.childName} <CloseX onClick={onClose} />
        </div>

        <div className="f-grp">
          <div style={{ fontSize: 11, color: "var(--ghost)", marginBottom: 4 }}>{t("assessments.parent")}</div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{a.user?.name ?? a.parentName ?? "—"}</div>
          <div style={{ fontSize: 11, color: "var(--ghost)" }}>{a.user?.email ?? a.parentEmail ?? "—"}</div>
          {(a.user?.phone ?? a.parentPhone) && (
            <div style={{ fontSize: 11, color: "var(--ghost)" }}>{a.user?.phone ?? a.parentPhone}</div>
          )}
        </div>

        <div className="f-grp" style={{ display: "flex", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--ghost)", marginBottom: 4 }}>{t("assessments.dob")}</div>
            <div style={{ fontSize: 13 }}>{a.dateOfBirth ?? "—"}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--ghost)", marginBottom: 4 }}>{t("assessments.gender")}</div>
            <div style={{ fontSize: 13 }}>{a.gender ?? "—"}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--ghost)", marginBottom: 4 }}>{t("assessments.country")}</div>
            <div style={{ fontSize: 13 }}>{a.country ?? "—"}</div>
          </div>
        </div>

        <div className="f-grp">
          <div style={{ fontSize: 11, color: "var(--ghost)", marginBottom: 4 }}>{t("assessments.domainScores")}</div>
          {Object.entries(a.domainScores ?? {}).map(([key, val]) => (
            <div key={key} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0" }}>
              <span style={{ textTransform: "capitalize" }}>{key.replace(/([A-Z])/g, " $1")}</span>
              <span style={{ fontWeight: 700 }}>{val}%</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "6px 0 0", borderTop: "1px solid var(--border)", marginTop: 6 }}>
            <span style={{ fontWeight: 700 }}>{t("assessments.overall")}</span>
            <span className={scoreBadgeClass(pct)}>{pct}%</span>
          </div>
        </div>

        {a.recommendedProgram && (
          <div className="f-grp">
            <div style={{ fontSize: 11, color: "var(--ghost)", marginBottom: 4 }}>{t("assessments.recommendedProgram")}</div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{a.recommendedProgram}</div>
          </div>
        )}

        {(a.strengths ?? []).length > 0 && (
          <div className="f-grp">
            <div style={{ fontSize: 11, color: "var(--ghost)", marginBottom: 4 }}>{t("assessments.strengths")}</div>
            <div style={{ fontSize: 12 }}>{a.strengths.join(", ")}</div>
          </div>
        )}

        {(a.areasToSupport ?? []).length > 0 && (
          <div className="f-grp">
            <div style={{ fontSize: 11, color: "var(--ghost)", marginBottom: 4 }}>{t("assessments.areasToSupport")}</div>
            <div style={{ fontSize: 12 }}>{a.areasToSupport.join(", ")}</div>
          </div>
        )}

        <div className="f-grp">
          <div style={{ fontSize: 11, color: "var(--ghost)", marginBottom: 4 }}>{t("assessments.uploads")}</div>
          <div style={{ fontSize: 12 }}>
            {a.videoCount ?? 0} {t("assessments.videos")} · {a.photoCount ?? 0} {t("assessments.photos")} · {a.reportCount ?? 0} {t("assessments.reports")}
          </div>
        </div>

        <button className="btn btn-out" style={{ width: "100%" }} onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}

// ─── PAGE ──────────────────────────────────────────────────────────────────────

export default function PageAssessments({ search: globalSearch }) {
  const { t } = useLang();
  const [search, setSearch] = useState(globalSearch ?? "");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const limit = 20;

  const debSearch = useDebounce(search, 400);
  useEffect(() => { setPage(1); }, [debSearch]);

  const { data, loading, reload } = useApi(
    () => assessmentsApi.list({ page, limit, ...(debSearch && { search: debSearch }) }),
    [page, debSearch]
  );

  const rows = data?.assessments ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <div className="sec-hd">
        <div>
          <div className="sec-ttl"><ClipboardList size={16} style={{ verticalAlign: "middle", marginRight: 6 }} />{t("nav.assessments")}</div>
          <div className="sec-sub">{t("assessments.found", { n: total.toLocaleString() })}</div>
        </div>
        <button className="btn btn-out btn-sm" onClick={reload}><RefreshCw size={12} /></button>
      </div>

      <div className="filter-bar">
        <div className="tb-search" style={{ maxWidth: 320, flex: 1 }}>
          <Search size={14} color="var(--ghost)" />
          <input
            placeholder={t("assessments.searchPlaceholder")}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)", display: "flex", alignItems: "center" }}>
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      <div className="card">
        {loading ? <Spinner /> : rows.length === 0
          ? <div style={{ padding: 24 }}><Empty icon="📋" message={t("assessments.noneFound")} /></div>
          : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("assessments.dateTime")}</th>
                    <th>{t("assessments.child")}</th>
                    <th>{t("tbl.user")}</th>
                    <th>{t("assessments.overall")}</th>
                    <th>{t("assessments.recommendedProgram")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(a => {
                    const pct = domainAvg(a.domainScores);
                    return (
                      <tr data-testid="assessment-row" key={a.id}>
                        <td><span style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>{fmtTime(a.createdAt)}</span></td>
                        <td><span style={{ fontWeight: 700, fontSize: 12 }}>{a.childName}</span></td>
                        <td>
                          <div style={{ fontWeight: 700, fontSize: 12 }}>{a.user?.name ?? "—"}</div>
                          <div style={{ fontSize: 10, color: "var(--ghost)" }}>{a.user?.email ?? "—"}</div>
                        </td>
                        <td><span className={scoreBadgeClass(pct)}>{pct}%</span></td>
                        <td><span style={{ fontSize: 12 }}>{a.recommendedProgram ?? "—"}</span></td>
                        <td><button className="btn btn-out btn-xs" onClick={() => setSelected(a)}>{t("assessments.viewDetails")}</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

        <Pagination page={page} totalPages={totalPages} total={total} onPrev={() => setPage(p => p - 1)} onNext={() => setPage(p => p + 1)} />
      </div>

      {selected && <DetailModal assessment={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
